import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATO = {
  IN_ATTESA: { label: 'In attesa di approvazione', cls: 'bg-yellow-100 text-yellow-800' },
  APPROVATO: { label: 'Pubblicato', cls: 'bg-green-100 text-green-800' },
  RIFIUTATO: { label: 'Non pubblicato', cls: 'bg-red-100 text-red-800' },
  ARCHIVIATO: { label: 'Archiviato', cls: 'bg-gray-100 text-gray-700' },
};
const quando = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');

// Bacheca dei condomini: il condomino scrive, il messaggio diventa visibile agli altri solo dopo l'approvazione dell'amministratore.
// L'amministratore riceve una notifica nella campanella dell'app (nessuna email).
export const BachecaCondomini = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const ruolo = selectedCondominium?.role;
  const admin = ruolo === 'ADMIN' || ruolo === 'ACCOUNTANT';
  const puoApprovare = ruolo === 'ADMIN';
  const base = `${API_URL}/api/condominiums/${condoId}/bacheca-condomini/messaggi`;
  const intest = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [messaggi, setMessaggi] = useState([]);
  const [titolo, setTitolo] = useState('');
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [filtro, setFiltro] = useState('IN_ATTESA');
  const [mostraAutore, setMostraAutore] = useState({});
  const [params] = useSearchParams();
  const [tipo, setTipo] = useState(params.get('nuova') === 'segnalazione' ? 'SEGNALAZIONE' : 'BACHECA');
  const [files, setFiles] = useState([]);
  const [rispostaAperta, setRispostaAperta] = useState(null);
  const [rispostaTesto, setRispostaTesto] = useState('');
  const [rispostaArchivia, setRispostaArchivia] = useState(true);

  const chiama = useCallback(async (url, corpo) => {
    const res = await fetch(url, { method: corpo === undefined ? 'GET' : 'POST', headers: intest, body: corpo === undefined ? undefined : JSON.stringify(corpo) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Operazione non riuscita');
    return json;
  }, [intest]);

  const carica = useCallback(async () => {
    if (!condoId) return;
    try { setMessaggi((await chiama(base)).messaggi || []); } catch (e) { toast.error(e.message); }
  }, [base, chiama, condoId]);
  useEffect(() => { carica(); }, [carica]);

  const scrivi = async (e) => {
    e.preventDefault();
    setInvio(true);
    try {
      const r = await chiama(base, { titolo, testo, tipo });
      // allegati: uno alla volta, sul messaggio appena creato
      let nonCaricati = 0;
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const res = await fetch(`${base}/${r.messaggio.id}/allegati`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
        if (!res.ok) { nonCaricati += 1; const j = await res.json().catch(() => ({})); toast.error(`${f.name}: ${typeof j.detail === 'string' ? j.detail : 'non caricato'}`); }
      }
      toast.success((r.info || 'Messaggio inviato') + (nonCaricati ? ` ${nonCaricati} allegati non sono stati caricati.` : ''));
      setTitolo(''); setTesto(''); setFiles([]);
      await carica();
    } catch (err) { toast.error(err.message); }
    setInvio(false);
  };

  const azione = async (id, cosa, corpo = {}) => {
    try { await chiama(`${base}/${id}/${cosa}`, corpo); await carica(); toast.success('Fatto'); } catch (err) { toast.error(err.message); }
  };

  const apriRisposta = (id) => { setRispostaAperta(id); setRispostaTesto(''); setRispostaArchivia(true); };
  const inviaRisposta = async (id) => {
    try {
      await chiama(`${base}/${id}/rispondi`, { testo: rispostaTesto, archivia: rispostaArchivia });
      setRispostaAperta(null); setRispostaTesto('');
      await carica(); toast.success('Risposta salvata: la vede solo chi ha scritto');
    } catch (err) { toast.error(err.message); }
  };

  const scaricaAllegato = async (m, a) => {
    try {
      const res = await fetch(`${base}/${m.id}/allegati/${a.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Allegato non disponibile');
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank');
    } catch (e) { toast.error(e.message); }
  };

  const visibili = admin ? messaggi.filter((m) => !filtro || m.stato === filtro) : messaggi;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bacheca dei condomini"
        description={admin ? 'Approva i messaggi scritti dai condomini prima che gli altri li vedano.' : 'Scrivi all\'amministrazione e agli altri condomini. Il messaggio sarà visibile agli altri solo dopo l\'approvazione.'}
      />

      {!admin && (
        <Card>
          <CardHeader><CardTitle className="text-base">Scrivi all'amministrazione</CardTitle>
            <CardDescription>
              {tipo === 'SEGNALAZIONE'
                ? 'La segnalazione resta riservata: la vedono solo tu e l\'amministratore. Allega le prove dei pagamenti, se le hai.'
                : 'Lo legge prima l\'amministratore. Se lo approva, compare nella bacheca di tutti.'}
            </CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={scrivi} className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant={tipo === 'SEGNALAZIONE' ? 'default' : 'outline'} onClick={() => setTipo('SEGNALAZIONE')}>Segnalazione su quote o pagamenti (riservata)</Button>
                <Button type="button" size="sm" variant={tipo === 'BACHECA' ? 'default' : 'outline'} onClick={() => setTipo('BACHECA')}>Messaggio per la bacheca</Button>
              </div>
              <div><Label>Titolo</Label><Input value={titolo} maxLength={150} onChange={(e) => setTitolo(e.target.value)} required /></div>
              <div><Label>Messaggio</Label><Textarea value={testo} maxLength={4000} rows={5} onChange={(e) => setTesto(e.target.value)} required /></div>
              <div>
                <Label>Allegati (PDF, JPG, PNG, WebP: fino a 5 file da 8 MB)</Label>
                <Input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))} />
                {files.length > 0 && <p className="text-xs text-muted-foreground mt-1">{files.map((f) => f.name).join(', ')}</p>}
              </div>
              <Button type="submit" disabled={invio || !titolo.trim() || !testo.trim()}>{tipo === 'SEGNALAZIONE' ? 'Invia la segnalazione' : 'Invia per approvazione'}</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {admin && (
        <div className="flex flex-wrap gap-2">
          {['IN_ATTESA', 'APPROVATO', 'RIFIUTATO', 'ARCHIVIATO', ''].map((k) => (
            <Button key={k || 'tutti'} size="sm" variant={filtro === k ? 'default' : 'outline'} onClick={() => setFiltro(k)}>
              {k ? STATO[k].label : 'Tutti'}{k ? ` (${messaggi.filter((m) => m.stato === k).length})` : ''}
            </Button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {visibili.length === 0 && <p className="text-sm text-muted-foreground">Nessun messaggio.</p>}
        {visibili.map((m) => (
          <Card key={m.id}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{m.titolo}</CardTitle>
                <div className="flex gap-2">
                  {m.tipo === 'SEGNALAZIONE' && <Badge className="bg-orange-100 text-orange-800">Segnalazione riservata</Badge>}
                  {(admin || m.mio) && <Badge className={STATO[m.stato]?.cls}>{m.tipo === 'SEGNALAZIONE' && m.stato === 'IN_ATTESA' ? 'Ricevuta' : (STATO[m.stato]?.label || m.stato)}</Badge>}
                </div>
              </div>
              <CardDescription>{m.autore}{m.unita ? ` · ${m.unita}` : ''} · {quando(m.creato_il)}{m.mio ? ' · tuo' : ''}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm whitespace-pre-wrap">{m.testo}</p>
              {(admin || m.mio) && m.stato === 'RIFIUTATO' && m.motivo_rifiuto && <p className="text-sm text-red-700">Motivo: {m.motivo_rifiuto}</p>}
              {(admin || m.mio) && m.risposta && (
                <div className="rounded-md border border-primary/30 bg-muted p-3 text-sm">
                  <p className="font-medium">Risposta dell'amministratore · {quando(m.risposta.il)}</p>
                  <p className="whitespace-pre-wrap">{m.risposta.testo}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{admin ? 'La vede solo chi ha scritto.' : 'È una risposta privata: la vedi solo tu.'}</p>
                </div>
              )}
              {(m.allegati || []).length > 0 && (
                <div className="flex flex-wrap gap-2 text-sm">
                  {m.allegati.map((a) => <Button key={a.id} type="button" size="sm" variant="outline" onClick={() => scaricaAllegato(m, a)}>{a.nome}</Button>)}
                </div>
              )}
              {puoApprovare && m.stato === 'IN_ATTESA' && m.tipo === 'SEGNALAZIONE' && (
                <Button size="sm" variant="outline" onClick={() => azione(m.id, 'archivia')}>Segna come gestita (archivia)</Button>
              )}
              {puoApprovare && m.stato === 'IN_ATTESA' && m.tipo !== 'SEGNALAZIONE' && (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={Boolean(mostraAutore[m.id])} onChange={(e) => setMostraAutore({ ...mostraAutore, [m.id]: e.target.checked })} />
                    Mostra nome e unità agli altri
                  </label>
                  <Button size="sm" onClick={() => azione(m.id, 'approva', { mostra_autore: Boolean(mostraAutore[m.id]) })}>Approva e pubblica</Button>
                  <Button size="sm" variant="outline" onClick={() => { const motivo = window.prompt('Motivo del rifiuto (lo vede solo chi ha scritto):'); if (motivo !== null) azione(m.id, 'rifiuta', { motivo }); }}>Rifiuta</Button>
                </div>
              )}
              {puoApprovare && rispostaAperta === m.id && (
                <div className="space-y-2 rounded-md border p-3">
                  <Label>Risposta privata (la vede solo chi ha scritto; nessuna email, arriva una notifica nell'app)</Label>
                  <Textarea value={rispostaTesto} maxLength={2000} rows={4} onChange={(e) => setRispostaTesto(e.target.value)} />
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={rispostaArchivia} onChange={(e) => setRispostaArchivia(e.target.checked)} />
                    Archivia il messaggio dopo la risposta
                  </label>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={!rispostaTesto.trim()} onClick={() => inviaRisposta(m.id)}>Salva la risposta</Button>
                    <Button size="sm" variant="outline" onClick={() => setRispostaAperta(null)}>Annulla</Button>
                  </div>
                </div>
              )}
              {puoApprovare && rispostaAperta !== m.id && m.stato !== 'ARCHIVIATO' && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => apriRisposta(m.id)}>{m.risposta ? 'Nuova risposta privata' : 'Rispondi in privato'}</Button>
                  {m.stato !== 'IN_ATTESA' && <Button size="sm" variant="outline" onClick={() => azione(m.id, 'archivia')}>Archivia</Button>}
                  {m.stato === 'IN_ATTESA' && m.tipo !== 'SEGNALAZIONE' && <Button size="sm" variant="outline" onClick={() => azione(m.id, 'archivia')}>Archivia senza pubblicare</Button>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BachecaCondomini;
